import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { usePortalAuth } from '../../context/PortalAuthContext';

function CustomerResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = usePortalAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  // Show error if no token
  if (!token) {
    return (
      <div className="portal-auth-page">
        <div className="portal-auth-container">
          <div className="portal-auth-header">
            <h1>Invalid Link</h1>
            <p>This reset link is invalid or has expired.</p>
          </div>
          <Link to="/portal/forgot-password" className="portal-btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Request New Link
          </Link>
        </div>
        <style>{portalStyles}</style>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await resetPassword(token, password);

    if (result.success) {
      // The server names the company the reset was for.
      const done = result.message || 'Password reset successful. You can now log in.';
      navigate(`/portal/login?message=${encodeURIComponent(done)}`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="portal-auth-page">
      <div className="portal-auth-container">
        <div className="portal-auth-header">
          <h1>Set New Password</h1>
          <p>Enter your new password below</p>
        </div>

        {error && (
          <div className="portal-message error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="portal-auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Re-enter your password"
            />
          </div>

          <button type="submit" className="portal-btn-primary" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="portal-auth-links">
          <Link to="/portal/login">Back to Login</Link>
        </div>
      </div>
      <style>{portalStyles}</style>
    </div>
  );
}

const portalStyles = `
  .portal-auth-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 20px;
  }
  .portal-auth-container {
    background: white;
    border-radius: 16px;
    padding: 40px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }
  .portal-auth-header {
    text-align: center;
    margin-bottom: 32px;
  }
  .portal-auth-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 8px 0;
  }
  .portal-auth-header p {
    color: #64748b;
    margin: 0;
    font-size: 0.95rem;
  }
  .portal-message.error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    font-size: 0.9rem;
  }
  .portal-auth-form .form-group {
    margin-bottom: 20px;
  }
  .portal-auth-form label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  .portal-auth-form input {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    box-sizing: border-box;
  }
  .portal-auth-form input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
  .portal-btn-primary {
    width: 100%;
    padding: 14px 20px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
  }
  .portal-btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  }
  .portal-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .portal-auth-links {
    text-align: center;
    margin-top: 24px;
  }
  .portal-auth-links a {
    color: #3b82f6;
    text-decoration: none;
    font-size: 0.9rem;
  }
  .portal-auth-links a:hover {
    text-decoration: underline;
  }
`;

export default CustomerResetPassword;
